import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useNotification } from '../contexts/NotificationContext';
import { segmentService } from '../services/segmentService';
import { Segment } from '../types/segment';

interface SegmentSelectorProps {
  selectedSegments: string[];
  onSegmentsChange: (segmentIds: string[]) => void;
  placeholder?: string;
  maxSegments?: number;
  className?: string;
  workspaceId?: string;
}

const SegmentSelector: React.FC<SegmentSelectorProps> = ({
  selectedSegments,
  onSegmentsChange,
  placeholder = "Select segments...",
  maxSegments,
  className = "",
  workspaceId
}) => {
  const { currentWorkspace } = useWorkspace();
  const { showError } = useNotification();
  const [segments, setSegments] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredSegments, setFilteredSegments] = useState<Segment[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  const resolvedWorkspaceId = workspaceId !== undefined ? workspaceId : currentWorkspace?.id;

  const loadSegments = useCallback(async () => {
    if (!resolvedWorkspaceId) return;

    try {
      setLoading(true);
      const workspaceSegments = await segmentService.getSegments({
        workspaceId: resolvedWorkspaceId,
        isActive: true
      });
      setSegments(workspaceSegments);
    } catch (error: any) {
      console.error('Error loading segments:', error);
      // Suppress the error if it's a permission issue (e.g., trying to read from a locked workspace)
      // otherwise, we might spam the user with "Failed to load segments" toasts
      if (error?.code !== 'permission-denied' && !error?.message?.includes('permission')) {
        showError('Failed to load segments');
      }
      setSegments([]);
    } finally {
      setLoading(false);
    }
  }, [resolvedWorkspaceId, showError]);

  useEffect(() => {
    if (resolvedWorkspaceId) {
      loadSegments();
    }
  }, [resolvedWorkspaceId, loadSegments]);

  // Filter segments based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredSegments(segments);
    } else {
      const filtered = segments.filter(segment =>
        segment.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !selectedSegments.includes(segment.id)
      );
      setFilteredSegments(filtered);
    }
  }, [segments, searchTerm, selectedSegments]);

  const handleSegmentToggle = (segmentId: string) => {
    if (selectedSegments.includes(segmentId)) {
      onSegmentsChange(selectedSegments.filter(id => id !== segmentId));
    } else {
      if (maxSegments && selectedSegments.length >= maxSegments) {
        return;
      }
      onSegmentsChange([...selectedSegments, segmentId]);
    }
  };

  const handleRemoveSegment = (segmentId: string) => {
    onSegmentsChange(selectedSegments.filter(id => id !== segmentId));
  };

  const getSelectedSegmentObjects = () => {
    return segments.filter(segment => selectedSegments.includes(segment.id));
  };

  const selectedSegmentObjects = getSelectedSegmentObjects();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (!resolvedWorkspaceId) {
    return (
      <div className={`text-sm text-gray-500 dark:text-neutral-400 ${className}`}>
        Please select a workspace to choose segments
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {/* Selected Segments Display */}
      <div
        className="min-h-[40px] border border-gray-300 dark:border-neutral-600 rounded-lg p-2 cursor-pointer bg-white dark:bg-neutral-700 hover:border-gray-400 dark:hover:border-neutral-500 focus-within:ring-2 focus-within:ring-gray-500 focus-within:border-transparent"
        onClick={() => setIsOpen(!isOpen)}
      >
        {selectedSegmentObjects.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {selectedSegmentObjects.map((segment) => (
              <span
                key={segment.id}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300"
              >
                {segment.name}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveSegment(segment.id);
                  }}
                  className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-purple-200 dark:hover:bg-purple-800"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            ))}
          </div>
        ) : (
          <span className="text-gray-500 dark:text-neutral-400 text-sm">{placeholder}</span>
        )}
        <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
          <svg
            className={`w-4 h-4 text-gray-400 dark:text-neutral-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-neutral-800 border border-gray-300 dark:border-neutral-600 rounded-lg shadow-lg max-h-60 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200 dark:border-neutral-700">
            <input
              type="text"
              placeholder="Search segments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Segments List */}
          <div className="max-h-48 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-sm text-gray-500 dark:text-neutral-400">
                Loading segments...
              </div>
            ) : filteredSegments.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500 dark:text-neutral-400">
                {searchTerm ? 'No segments found' : 'No segments available'}
              </div>
            ) : (
              filteredSegments.map((segment) => (
                <div
                  key={segment.id}
                  onClick={() => {
                    handleSegmentToggle(segment.id);
                    setSearchTerm('');
                  }}
                  className={`px-4 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-neutral-700 flex items-center justify-between ${selectedSegments.includes(segment.id) ? 'bg-gray-50 dark:bg-neutral-700' : ''
                    }`}
                >
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: segment.color || '#9333ea' }}
                    />
                    <span className="text-sm text-gray-900 dark:text-white">{segment.name}</span>
                    {segment.isDynamic && (
                      <span className="text-xs text-gray-500 dark:text-neutral-400">(Dynamic)</span>
                    )}
                  </div>
                  {selectedSegments.includes(segment.id) && (
                    <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SegmentSelector;




